<template>
  <form
    :id="formId"
    ref="form"
    :action="formAction"
    :class="['generic-form', cssClassForm]"
    :name="formId"
    novalidate
    @submit.prevent="submitEvent"
    @keydown="handleClickEvent()"
    @click="handleClickEvent()"
  >
    <!-- Header -->
    <slot v-if="$slots.header" name="header"></slot>

    <!-- Row -->
    <div class="generic-form__row">
      <!-- Sections / Columns -->
      <section
        v-for="(section, index) in validatedSections"
        v-show="!section.hidden"
        :key="index"
        class="generic-form__section"
        :class="[section.cssClass]"
      >
        <!-- Section Title -->
        <component
          :is="section.titleTag"
          v-if="section.title"
          class="generic-form__section-title"
        >
          {{ section.title }}
        </component>

        <!-- Fields -->
        <div
          v-for="(field, fIndex) in section.fields"
          :key="fIndex"
          class="generic-form__field"
        >
          <component
            :is="typeMap[field.type]"
            v-model="formData[field.id]"
            v-bind="field"
            :readonly="field.readOnly"
            @input="field.edited = true"
            @reset-errors="resetErrors(field)"
          >
            <template v-if="field.label">
              {{ field.label }}
            </template>
            <template v-if="field.helpText" #helperText>
              <div
                class="generic-form__helper-text"
                v-htmlsafe="field.helpText"
              ></div>
            </template>
          </component>
        </div>

        <!-- Subsections -->
        <div class="generic-form__subsection">
          <div
            v-for="(subSection, sIndex) in section.subSections"
            :key="sIndex"
            class="generic-form__wrapper"
            :class="[subSection.cssClass]"
          >
            <component
              :is="subSection.titleTag"
              v-if="subSection.title"
              class="generic-form__wrapper__title"
            >
              {{ subSection.title }}
            </component>
            <div
              v-for="(subSectionField, sFIndex) in subSection.fields"
              :key="sFIndex"
              class="generic-form__field"
            >
              <component
                :is="typeMap[subSectionField.type]"
                :model-value="formData[subSectionField.id]"
                :v-model="formData[subSectionField.id]"
                @updated="updateFormData"
                :readonly="subSectionField.readOnly"
                v-bind="subSectionField"
                @reset-errors="resetErrors(subSectionField)"
                :class="[subSectionField.cssClass]"
                @input="subSectionField.edited = true"
              >
                <template v-if="subSectionField.label">
                  {{ subSectionField.label }}
                </template>
                <template v-if="subSectionField.helpText" #helperText>
                  <div
                    class="generic-form__helper-text"
                    v-htmlsafe="subSectionField.helpText"
                  ></div>
                </template>
              </component>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Footer -->
    <slot v-if="$slots.footer" name="footer"></slot>

    <div class="generic-form__control">
      <button
        :class="['generic-form__submit', cssClassButton, submit.customClass]"
        :disabled="submitDisabled"
      >
        <span class="oap-button__label">
          {{ buttonLabel }}
        </span>
      </button>
    </div>

    <!-- error messages -->
    <ul
      v-show="errorMessageList"
      aria-live="polite"
      class="generic-form__error-messages"
    >
      <li
        v-for="(errors, $key) in errorMessageList"
        :key="$key"
        v-htmlsafe="errors"
      ></li>
    </ul>

    <!--recapctha-->
    <div
      v-if="hasRecaptcha"
      class="generic-form__recaptcha generic-form__helper-text"
    >
      <div
        v-if="hasRecaptcha"
        class="recaptcha"
        v-htmlsafe="recaptcha.legalText"
      ></div>
    </div>
  </form>
</template>
<script>
import Vue from 'vue';
import { recaptcha } from '../../scripts/mixins/recaptcha/recaptcha';
import { AnalyticsHandler } from '../../scripts/directives/analyticsHandler';
import { eventBus } from 'EventBus/';
import GenericInput from './generic-input.vue';
import GenericOptions from './generic-options.vue';
import GenericDatepicker from './generic-datepicker.vue';
import GenericSelect from './generic-select.vue';

export default {
  name: 'GenericForm',
  components: {
    GenericInput,
    GenericOptions,
    GenericDatepicker,
    GenericSelect,
  },
  mixins: [recaptcha],
  props: {
    data: {
      type: Object,
      required: true,
    },
    cssClassForm: {
      type: String,
      required: false,
      default: '',
    },
    cssClassButton: {
      type: String,
      required: false,
      default: 'button -primary',
    },
    messageTiming: {
      type: Number,
      required: false,
      default: 5,
    },
    isSingleNotification: {
      type: Boolean,
      default: false,
    },
    enableAntiForgery: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      richTextError: undefined,
      components: {
        GenericInput,
        GenericOptions,
        GenericDatepicker,
        GenericSelect,
      },
      errorMessageList: null,
      formData: {},
      isFormValid: true,
      items: [],
      typeMap: {},
      object: {},
      buttonLabel: null,
      submitDisabled: false,
      triggerErrorMessage: '',
      isFirstError: true,
    };
  },
  computed: {
    formId() {
      return this.object.formId;
    },
    recaptcha() {
      return this.object.recaptcha;
    },
    hasRecaptcha() {
      return this.object.recaptcha && this.object.recaptcha.hasRecaptcha;
    },
    submit() {
      return this.object.submit;
    },
    formAction() {
      return this.enableAntiForgery
        ? this.submit.apiUrl
        : this.submit.apiUrlWithoutAntiForgeryToken;
    },
    validatedSections: {
      get: function () {
        return this.object.sections;
      },
      set: function (nSections) {
        this.object.sections = nSections;
      },
    },
  },
  created() {
    this.loadTypeMap();
    this.init();
  },
  mounted() {
    /* istanbul ignore next */
    if (this.hasRecaptcha) {
      this.loadRecaptchaOnInput(this.$refs.form, this.recaptcha.siteKey);
    }
    if (this.object.forceValidation) {
      this.validateFormData(true);
    }
  },

  destroyed() {
    // here we return the initial value of the properties which will be used after re-renderin
    this.data.sections.forEach((section) => {
      section.fields.forEach((field) => {
        field.edited = false;
        field.errorMessages = [];
      });
    });
  },

  methods: {
    handleClickEvent() {
      eventBus.$emit('form:clicked');
    },
    updateFormData(payload) {
      const { fieldName, value, edited, pristine } = payload;
      this.formData[fieldName] = value;

      const field = this.items.find(f => f.id === fieldName);
      if (field) {
        if (typeof edited !== 'undefined') field.edited = edited;
        if (typeof pristine !== 'undefined') field.pristine = pristine;

        // only validate this one field
        this.validateFormItem(field, false);
      }
    },
    loadTypeMap() {
      const GENERIC_INPUT_TYPE = 'generic-input';
      const GENERIC_OPTIONS_TYPE = 'generic-options';
      const GENERIC_DATEPICKER_TYPE = 'generic-datepicker';
      const GENERIC_SELECT_TYPE = 'generic-select';

      this.typeMap = {
        ...this.typeMap,
        text: GENERIC_INPUT_TYPE,
        email: GENERIC_INPUT_TYPE,
        password: GENERIC_INPUT_TYPE,
        textarea: GENERIC_INPUT_TYPE,
        radio: GENERIC_OPTIONS_TYPE,
        checkbox: GENERIC_OPTIONS_TYPE,
        datepicker: GENERIC_DATEPICKER_TYPE,
        dropdown: GENERIC_SELECT_TYPE,
      };
    },
    init() {
      this.object = this.data;
      let fieldArray = [];

      this.object.sections.forEach((section) => {
        fieldArray = [].concat(section.fields, fieldArray);
        section.fields.forEach((field) => {
          this.fixFieldRequiredValidation(field);
        });

        if (section.subSections) {
          section.subSections.forEach((subSection) => {
            fieldArray = [].concat(subSection.fields, fieldArray);
            subSection.fields.forEach((field) => {
              this.fixFieldRequiredValidation(field);
            });
          });
        }
      });
      this.items = Array.from(fieldArray);

      //set button label
      this.updateSubmitLabel(
        this.submit.submitReadonly ? this.submit.submitReadonly : false
      );
    },
    fixFieldRequiredValidation(field) {
      if (field.defaultValue) {
        this.formData[field.id] = field.defaultValue;
      }

      Vue.set(
        this.formData,
        field.id,
        field.defaultValue ? field.defaultValue : null
      );

      //Get values from requiredValidation
      if (field.requiredValidation) {
        field.required = field.requiredValidation.isRequired;
        field.requiredMessage = field.requiredValidation.message;
      }

      //date field uses input format instead for dropdown
      if (field.type === 'datepicker') {
        field.inputFormat = field.useDateInputFormat;
      }

      //Get datepicker specific validation values
      if (field.type === 'datepicker' && field.allFieldsRequired) {
        field.allDatesRequired = field.allFieldsRequired.isRequired;
        field.missingDateMessage = field.allFieldsRequired.message;
      }

      //Get values from dateValidation
      if (field.dateValidation) {
        field.startYear = field.dateValidation.startYear;
        field.minAge = field.dateValidation.minAge;
        field.minAgeValidationMessage =
          field.dateValidation.minAgeValidationMessage;
      }
    },
    getUserIdentifier(payload) {
      const fieldIdentifier = this.items.find((field) => field.isUserId);
      const userIdFromPayload = fieldIdentifier
        ? payload.fields.find((field) => field.id == fieldIdentifier.id)
        : null;

      return userIdFromPayload ? userIdFromPayload.value : '';
    },
    async submitEvent() {
      const payload = this.formatFormData(this.formData);
      const requestToken = this.$refs.form.querySelector(
        'input[name="__RequestVerificationToken"]'
      )?.value;

      this.validateFormData(true);

      if (!this.isFormValid) {
        this.focusFirstInvalidField();
      } else {
        this.errorMessageList = [];
        this.updateSubmitLabel(true);

        if (this.hasRecaptcha) {
          payload.recaptchaToken = await this.generateToken(
            this.recaptcha.siteKey
          );
        }

        payload.userId = this.getUserIdentifier(payload);

        payload.IsGpcEnabled = false;

        if (window.OneTrust) {
          payload.IsGpcEnabled =
            navigator.globalPrivacyControl &&
            window.OneTrust.GetDomainData().Groups.some(
              (data) => data.IsGpcEnabled
            );
        }

        /* istanbul ignore next */
        fetch(this.formAction, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json;charset=utf-8',
            'req-string-client': requestToken,
          },
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        })
          .then(this.parseJSON)
          .then((data) => {
            if (data.Data.success) {
              this.handlerAnalytics(this.data.submit.eventTagging);
              this.updateSubmitLabel(false);
              this.handleSuccess(data.Data.redirectUrl);
              eventBus.$emit('form:submitted');
              return;
            }
            this.handleValidationErrorMessages(data);
            this.updateSubmitLabel(false);
          })
          .catch((error) => {
            console.error('Could not load answer', error); // eslint-disable-line no-console
            this.submitErrorMessages();
            this.updateSubmitLabel(false);
          });
      }
    },
    submitErrorMessages() {
      this.errorMessageList = [];
      this.errorMessageList.push(this.submit.errorMessage);
    },
    handleValidationErrorMessages(data) {
      const hasValidationErrors =
        data.Data.validationMessages && data.Data.validationMessages.length > 0;

      if (hasValidationErrors) {
        this.errorMessageList = [];

        data.Data.validationMessages.forEach((msg) => {
          this.errorMessageList.push(msg);
        });
      } else {
        this.submitErrorMessages();
      }
    },
    formatFormData(formData) {
      const mappedFields = [];

      Object.keys(formData).forEach((key) =>
        mappedFields.push({ id: key, value: formData[key] })
      );

      return {
        id: this.object.formId,
        sitecoreContextItemId: this.object.sitecoreContextItemId,
        fields: mappedFields,
      };
    },
    parseJSON(response) {
      return response.json();
    },
    handleSuccess(redirectPage) {
      if (this.submit.successPage) {
        window.location.href = redirectPage
          ? redirectPage
          : this.submit.successPage;
      }
    },
    validateFormData(forceErrorVerification) {
      if (!this.items) {
        return;
      }
      this.isFirstError = true;
      //reset form valid value
      this.isFormValid = true;

      //iterate all items
      this.items.forEach((item) => {
        this.validateFormItem(item, forceErrorVerification);
      });
    },
    errorsBasedOnRegex(itemValue, item) {
      if (!(item.validations && itemValue)) {
        return [];
      }

      const errorMessages = [];
      item.validations.forEach((validation) => {
        const regexp = new RegExp(validation.regex);
        if (!itemValue.match(regexp)) {
          errorMessages.push(validation.message);
        }
      });

      return errorMessages.length > 0 ? errorMessages : [];
    },
    errorsBasedOnEquality(itemValue, item) {
      const hasEqualityCheck =
        item.equalsValidation &&
        item.equalsValidation['equalsTo'] &&
        item.equalsValidation['message'] &&
        item.equalsValidation['message'].length > 0;

      if (!hasEqualityCheck) {
        return [];
      }

      const referenceFieldId = item.equalsValidation['equalsTo'];
      const referenceFieldValue = this.formData[referenceFieldId];
      const valid = itemValue === referenceFieldValue;

      return referenceFieldValue && !valid
        ? item.equalsValidation['message']
        : [];
    },
    dateStringToObject(hasDateSelected, itemValue) {
      if (!hasDateSelected) {
        return false;
      }
      const dateValues = itemValue.split('/');
      const month = parseInt(dateValues[0]),
        day = parseInt(dateValues[1]),
        year = parseInt(dateValues[2]);
      return { day: day, month: month, year: year };
    },
    errorsForDatepicker(itemValue, item) {
      if (item.type !== 'datepicker') {
        return [];
      }

      const errorMessages = [];
      const date = itemValue;
      const validInputValue = (itemValue) => {
        if (itemValue === null) return false;
        if (typeof itemValue === 'string') return true;
      };
      const allDatesRequired =
        item.allDatesRequired &&
        item.missingDateMessage &&
        item.pristine === false &&
        !validInputValue(date);
      const isRequired =
        item.required &&
        item.requiredMessage &&
        item.edited &&
        !validInputValue(date);
      const hasAgeErrorVerification =
        validInputValue(date) &&
        item.minAge &&
        item.minAge > 0 &&
        item.minAgeValidationMessage;

      if (isRequired) {
        errorMessages.push(item.requiredMessage);
      }

      if (allDatesRequired) {
        errorMessages.push(item.missingDateMessage);
      }

      if (hasAgeErrorVerification) {
        // eslint-disable-next-line no-useless-escape
        const result = date.match(/[0-9]{2}([\/])[0-9]{2}[\/][0-9]{4}/);
        let dateSplitted = {};
        if (result) {
          const split = result[0].split(result[1]);
          dateSplitted.day = +split[1];
          dateSplitted.month = +split[0];
          dateSplitted.year = +split[2];
        }
        const calculatedYear = dateSplitted.year + item.minAge;
        const calculatedMonth = dateSplitted.month - 1;
        const currentDate = new Date();
        const selectedDate = new Date(
          calculatedYear,
          calculatedMonth,
          dateSplitted.day
        );

        if (currentDate.getTime() < selectedDate.getTime()) {
          errorMessages.push(item.minAgeValidationMessage);
        }
      }

      return errorMessages;
    },
    errorsForRequiredFields(itemValue, item) {
      if (item.type === 'datepicker') {
        return [];
      }

      const hasRequiredMessage = item.required && item.requiredMessage;
      const isEmptyOrNull = !itemValue || itemValue.length === 0;

      return item.edited && hasRequiredMessage && isEmptyOrNull
        ? item.requiredMessage
        : [];
    },
    resetErrors(fieldItem) {
      if (!fieldItem.errorMessages.length) return;
      this.items.forEach((item) => {
        if (item.id !== fieldItem.id) return;
        fieldItem.errorMessages.splice(0);
      });
    },
    validateFormItem(item, forceErrorVerification) {
      const itemValue = this.formData[item.id];

      //force error verification
      if (!item.edited) {
        item.edited = forceErrorVerification;
      }

      const errorMessages = [].concat(
        this.errorsBasedOnRegex(itemValue, item),
        this.errorsBasedOnEquality(itemValue, item),
        this.errorsForDatepicker(itemValue, item),
        this.errorsForRequiredFields(itemValue, item)
      );

      Vue.set(item, 'errorMessages', errorMessages);

      if (errorMessages && errorMessages.length) {
        this.isFormValid = false;
        if (this.isFirstError) {
          this.triggerErrorMessage = errorMessages;
          this.isFirstError = false;
        }
      }
    },
    async focusFirstInvalidField() {
      this.$nextTick(() => {
        const firstInvalidField = this.$refs.form.querySelectorAll(
          '[aria-invalid="true"]'
        )[0];

        if (firstInvalidField) {
          firstInvalidField.focus();
        }
      });
    },
    updateSubmitLabel(start) {
      this.buttonLabel = this.submit.submitReadonly
        ? this.submit.label
        : start && this.submit.requestLabel
        ? this.submit.requestLabel
        : this.submit.label;
      this.submitDisabled = start;
    },
    handlerAnalytics(eventTagging) {
      if (!eventTagging) {
        console.warn(`Form Event Tagging: Tagging object missing.`);
        return;
      }

      const hasOptionalProperties = eventTagging.optionalProperties;

      const dataToSend = {
        type: 'userActionEvent',
        ecommerce: eventTagging.ecommerce
          ? eventTagging.ecommerce
          : 'undefined',
        event_name:
          hasOptionalProperties && eventTagging.optionalProperties.name
            ? eventTagging.optionalProperties.name
            : '',
        referral:
          hasOptionalProperties && eventTagging.optionalProperties.referral
            ? eventTagging.optionalProperties.referral
            : '',
        form_name:
          hasOptionalProperties && eventTagging.optionalProperties.form_name
            ? eventTagging.optionalProperties.form_name
            : '',
      };

      AnalyticsHandler.getAnalyticsHandler().push(dataToSend);
    },
    touchInput(field) {
      field.edited = true;
      field.pristine = false;
    },
  },
};
</script>
